import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Building2, Users, FolderOpen, Wallet, ShieldAlert, Scale,
  Network, CalendarClock, CheckCircle2, FileSearch, Layers, Lock, Gauge, Sparkles,
  Landmark, ArrowLeftRight, Send, Smartphone, Car, Gem, Coins, Briefcase, KeyRound,
  Bitcoin, HeartHandshake, Stamp, Building, Clock, Loader2,
  ShieldCheck, Ban, Globe, UserCog, FileWarning, Siren, Newspaper, MapPin, ListChecks,
  Ticket, CreditCard, Plane, Palette, Truck, Ship, Package, HandCoins, Gift,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { VoraLogo } from "@/components/brand/VoraLogo";
import { api, apiError } from "@/lib/api";
import { cargarRegimenes, buscarRegimen } from "@/lib/regimenes";
import { toast } from "sonner";

import { HERO_LANDING as HERO } from "@/lib/imagenes";

const peso = (n) => `$${Number(n || 0).toLocaleString("es-MX")}`;
const planPrecio = (p) => p.es_cotizacion ? "Cotización" : `$${Number(p.precio_mensual || 0).toLocaleString("es-MX")}`;
// v4: se distingue Cartera (ilimitada) del consumo mensual de verificación (créditos = altas con screening).
// Filtra bullets residuales de modulos: números sueltos ("1") y duplicados de créditos ("X créditos incluidos").
const _esBulletCreditoDuplicado = (m) => {
  const s = String(m || "").trim();
  if (!s) return true;
  if (/^[\d.,\s]+$/.test(s)) return true; // números sueltos ("1", "1,500")
  const low = s.toLowerCase();
  return low.includes("crédito") && low.includes("incluid");
};
// sep-2026 · el bullet plano "Productos de crédito" del plan se sustituye por la estructura real
// (Esencial 1 · Profesional 5 · Empresarial ilimitado, extra $500/mes). Solo texto: los valores
// salen del plan del backend. Cualquier otro módulo se deja intacto.
const _lineaProductos = (p) => (m) => {
  if (String(m || "").trim() !== "Productos de crédito") return m;
  const n = Number(p.tipos_producto_incluidos);
  if (!Number.isFinite(n) || n < 0) return "Productos de crédito ilimitados";
  const extra = p.precio_tipo_adicional ? ` (+${peso(p.precio_tipo_adicional)}/mes por adicional)` : "";
  return `${n} producto${n === 1 ? "" : "s"} de crédito incluido${n === 1 ? "" : "s"}${extra}`;
};
const planItems = (p) => {
  const out = [];
  // Cartera total (base de clientes) — sin límite en todos los planes.
  out.push("Cartera de clientes ilimitada");
  // v4: 1 crédito = 1 alta de cliente nuevo con screening de listas (SIC/PLD/PUI).
  if (p.creditos_incluidos !== undefined && p.creditos_incluidos !== null && p.creditos_incluidos !== -1) {
    const n = Number(p.creditos_incluidos);
    const extra = (!p.es_cotizacion && p.precio_credito_adicional)
      ? ` (+${peso(p.precio_credito_adicional)} por alta adicional)` : "";
    out.push(`${n.toLocaleString("es-MX")} altas de clientes/mes con screening de listas${extra}`);
  } else if (p.creditos_incluidos === -1 && !p.es_cotizacion) {
    out.push("Altas de clientes/mes ilimitadas");
  }
  out.push("Usuarios ilimitados");
  return out.concat((p.modulos || []).filter((m) => !_esBulletCreditoDuplicado(m)).map(_lineaProductos(p)));
};

const FINANCIERAS_ICONOS = {
  Wallet, Landmark, ArrowLeftRight, Send, Smartphone, Building2,
};

// ago-2026 · las tarjetas de régimen se construyen con el catálogo del backend
// (`/api/meta` → `regimenes`). Este mapa solo resuelve el ícono por nombre.
const ICONOS = {
  Wallet, Landmark, ArrowLeftRight, Send, Smartphone, Building2, Building,
  Ticket, CreditCard, Plane, HandCoins, Gem, Palette, Car, ShieldCheck,
  Truck, Briefcase, Stamp, Gift, Ship, KeyRound, Bitcoin, ShieldAlert,
};

// feb-2026 · rename del vertical (Fr. V LFPIORPI). Constantes centralizadas para no
// hardcodear el string en múltiples lugares. El corto se usa en la tarjeta y la landing
// donde no cabe el largo.
export const LABEL_VERTICAL_INMO = "Intermediación y comercialización de bienes inmuebles";
export const LABEL_VERTICAL_INMO_CORTO = "Intermediación de inmuebles";


const MODULOS = [
  { icon: Building2, t: "Entidades & Multiempresa", d: "Alta de sujetos obligados con información aislada por entidad." },
  { icon: Users, t: "Clientes & KYC", d: "Personas físicas, PFAE y morales con perfil de riesgo y PEP/listas." },
  { icon: FolderOpen, t: "Expediente digital", d: "Checklist documental por cliente y por régimen, con vigencias." },
  { icon: Wallet, t: "Operación", d: "Crédito en SOFOM; operaciones y bienes por fracción en actividad vulnerable." },
  { icon: ShieldAlert, t: "PLD: CNBV y SAT-UIF", d: "Matriz de riesgo, EBR, motor de reglas, alertas y gestión de casos." },
  { icon: Send, t: "Avisos SPPLD", d: "Umbrales en UMA por fracción, aviso mensual, en ceros y de 24 horas." },
  { icon: Scale, t: "CONDUSEF (entidades financieras)", d: "SIPRES, RECA, RECO, REUNE y REDECO en un solo lugar." },
  { icon: CalendarClock, t: "Calendario regulatorio", d: "Obligaciones de tu supervisor con semáforo y alertas de vencimiento." },
];

const BENEFICIOS = [
  "Evidencia documental automática en cada operación",
  "Bitácoras inmutables y auditoría regulatoria",
  "Alertas automáticas por riesgo, vencimiento o inconsistencia",
  "Reportes y acuses centralizados",
  "Arquitectura multi-régimen y multiempresa",
  "Preparado para integración con autoridades y SIC",
];

function EstadoBadge({ estado }) {
  if (estado === "Disponible")
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-600/15 px-2.5 py-0.5 text-xs font-semibold text-forest-700">
        <CheckCircle2 className="h-3.5 w-3.5" /> Disponible
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-500">
      <Clock className="h-3.5 w-3.5" /> Próximamente
    </span>
  );
}

function DashboardMockup() {
  const bars = [42, 68, 55, 80, 60, 92, 74];
  return (
    <div className="relative">
      <div className="absolute -inset-5 rounded-3xl bg-sage/15 blur-2xl" />
      <div className="relative rounded-2xl border border-white/15 bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-forest-600" />
            <p className="font-heading text-sm font-semibold text-slate-800">Panel de cumplimiento</p>
          </div>
          <span className="rounded-full bg-forest-600/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-forest-700">En vivo</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[["Cartera", "$12.4M"], ["Alertas PLD", "8"], ["Cumpl. doc.", "94%"]].map(([a, b]) => (
            <div key={a} className="rounded-lg border border-slate-100 bg-[#F7F7F2] p-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">{a}</p>
              <p className="mt-0.5 font-heading text-lg font-bold text-primary">{b}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 flex items-end gap-2.5">
          {bars.map((h, i) => (
            <div key={`bar-${i}-${h}`} className="flex-1 rounded-t-md bg-gradient-to-t from-primary to-forest-600" style={{ height: `${h}px` }} />
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[10px] text-slate-400">
          <span>Ene</span><span>Feb</span><span>Mar</span><span>Abr</span><span>May</span><span>Jun</span><span>Jul</span>
        </div>
        <div className="mt-4 flex items-center gap-4 border-t border-slate-100 pt-3 text-xs">
          <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2 w-2 rounded-full bg-forest-600" /> Vigente</span>
          <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2 w-2 rounded-full bg-amber-400" /> Por vencer</span>
          <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2 w-2 rounded-full bg-red-400" /> Atención</span>
        </div>
      </div>
    </div>
  );
}

function RegimenCard({ item, onWaitlist }) {
  const Icon = ICONOS[item.icono] || ShieldAlert;
  const disponible = item.activo;
  return (
    <div data-testid={`regimen-card-${item.id}`}
         className={`rounded-xl border bg-white p-3.5 transition-all hover:shadow-sm ${disponible ? "border-forest-600/40 ring-1 ring-forest-600/10" : "border-slate-200"}`}>
      <div className="flex items-start gap-2.5">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${disponible ? "bg-primary text-sage" : "bg-slate-100 text-slate-400"}`}>
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          {item.fraccion && (
            <p className="text-[10px] font-semibold uppercase tracking-wide text-forest-600">Fr. {item.fraccion_display || item.fraccion}</p>
          )}
          <p className="font-heading text-sm font-semibold leading-tight text-slate-800">
            {item.nombre_corto || item.nombre}
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <EstadoBadge estado={disponible ? "Disponible" : "Próximamente"} />
        {disponible ? (
          <Link to={`/registro?regimen=${item.id}`}
                data-testid={`regimen-registrar-${item.id}`}
                className="text-[11px] font-medium text-forest-700 underline-offset-2 transition-colors hover:text-primary hover:underline">
            Registrar entidad
          </Link>
        ) : (
          <button
            data-testid={`waitlist-btn-${item.id}`}
            onClick={() => onWaitlist(item.nombre_corto || item.nombre)}
            className="text-[11px] font-medium text-forest-700 underline-offset-2 transition-colors hover:text-primary hover:underline"
          >
            Unirse a la lista de espera
          </button>
        )}
      </div>
    </div>
  );
}

function Logo() {
  return <VoraLogo variant="horizontal" tone="dark" />;
}

export default function Landing() {
  const [wlOpen, setWlOpen] = useState(false);
  const [wlRegimen, setWlRegimen] = useState("");
  const [wlEmail, setWlEmail] = useState("");
  const [wlSending, setWlSending] = useState(false);
  const [planRegimen, setPlanRegimen] = useState("sofom_enr");
  const [planes, setPlanes] = useState([]);
  const [financieras, setFinancieras] = useState([]);
  const [vulnerables, setVulnerables] = useState([]);

  useEffect(() => {
    api.get("/planes").then((r) => setPlanes(r.data)).catch(() => {});
    cargarRegimenes()
      .then(({ financieras: f, vulnerables: v }) => { setFinancieras(f); setVulnerables(v); })
      .catch(() => {});
  }, []);
  const catalogo = [...financieras, ...vulnerables];
  const regimenSel = buscarRegimen(catalogo, planRegimen);
  // El plan aplica al régimen si alguno de sus `regimenes` coincide con el id O con el
  // nombre del régimen elegido (case-insensitive).
  const _norm = (s) => String(s || "").toLowerCase();
  const planesDelRegimen = planes.filter((p) => {
    const regs = (p.regimenes || []).map(_norm);
    if (regs.length === 0 || !regimenSel) return false;
    return regs.includes(_norm(regimenSel.id)) || regs.includes(_norm(regimenSel.nombre));
  });
  // ago-2026 · ya no se compara contra literales de régimen: hay plan disponible si el
  // catálogo lo trae activo Y existe al menos un plan que lo cubra.
  const planDisponible = !!regimenSel?.activo && planesDelRegimen.length > 0;
  const esVulnerable = regimenSel?.grupo === "actividad_vulnerable";
  const nDisponibles = catalogo.filter((r) => r.activo).length;
  const nPreparacion = catalogo.filter((r) => !r.activo).length;
  const nombreRegimenSel = regimenSel?.nombre_corto || regimenSel?.nombre || "";

  const openWaitlist = (regimen) => { setWlRegimen(regimen); setWlEmail(""); setWlOpen(true); };

  const submitWaitlist = async () => {
    if (!wlEmail.trim() || !wlEmail.includes("@")) { toast.error("Captura un correo válido"); return; }
    setWlSending(true);
    try {
      await api.post("/waitlist", { email: wlEmail.trim(), regimen: wlRegimen });
      toast.success(`Listo. Te avisaremos cuando ${wlRegimen} esté disponible.`);
      setWlOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWlSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo />
          <nav className="hidden items-center gap-8 text-sm font-medium text-slate-600 md:flex">
            <a href="#regimenes" className="transition-colors hover:text-primary">Regímenes</a>
            <a href="#modulos" className="transition-colors hover:text-primary">Módulos</a>
            <a href="#integraciones" className="transition-colors hover:text-primary">Integraciones</a>
            <a href="#planes" className="transition-colors hover:text-primary">Planes</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button data-testid="landing-login" variant="ghost" className="text-slate-700">Iniciar sesión</Button></Link>
            <Link to="/registro"><Button data-testid="landing-register" className="bg-primary hover:bg-sidebar-hover">Registrar entidad</Button></Link>
          </div>
        </div>
      </header>

      {/* 1. Hero */}
      <section className="relative overflow-hidden bg-primary">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-primary" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:py-32">
          <div className="animate-fade-in-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage/10 px-3 py-1 text-xs font-medium text-sage">
              <Sparkles className="h-3.5 w-3.5" /> Plataforma multi-régimen para sujetos obligados
            </span>
            <h1 className="mt-6 font-heading text-4xl font-semibold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Cumplimiento regulatorio y operación, en una sola plataforma.
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-200">
              La plataforma de cumplimiento y operación para <span className="font-medium text-white">entidades financieras y actividades vulnerables</span> en México.
              <span className="text-sage"> Arquitectura multiempresa</span>: cada sujeto obligado opera de forma aislada, configurable y auditable, sobre una misma infraestructura.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/registro"><Button data-testid="hero-register" size="lg" className="bg-forest-600 text-white hover:bg-forest-700">
                Registrar mi entidad <ArrowRight className="ml-2 h-4 w-4" />
              </Button></Link>
              <Link to="/login"><Button size="lg" variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white/10">
                Ver demostración
              </Button></Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300">
              {["SOFOM ENR", "PLD / CNBV", "CONDUSEF", "LFPIORPI", "Círculo / Buró"].map((x) => (
                <span key={x} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-sage" /> {x}</span>
              ))}
            </div>
          </div>
          <div className="relative hidden items-center justify-center lg:flex">
            <DashboardMockup />
          </div>
        </div>
      </section>

      {/* 2. Una plataforma, todos los regímenes */}
      <section id="regimenes" className="bg-[#F7F7F2]">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wide text-forest-600">Multi-régimen</span>
            <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Una plataforma, todos los regímenes
            </h2>
            <p className="mt-4 text-base text-slate-600">
              FELVORA es la plataforma madre de cumplimiento para los sujetos obligados de México. Una sola
              arquitectura, multiempresa, que da servicio a entidades financieras y actividades vulnerables,
              donde cada régimen se habilita de forma ordenada sobre la misma base regulatoria.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm shadow-sm"
                 data-testid="chip-conteo-regimenes">
              <span className="flex items-center gap-1.5 font-semibold text-forest-700">
                <span className="h-2 w-2 rounded-full bg-forest-600" /> {nDisponibles} regímenes disponibles
              </span>
              <span className="h-3.5 w-px bg-slate-200" />
              <span className="flex items-center gap-1.5 text-slate-500">
                <span className="h-2 w-2 rounded-full bg-slate-300" /> {nPreparacion} en preparación
              </span>
            </div>
          </div>

          {/* Entidades financieras (columna centrada) */}
          <div className="mx-auto mt-14 max-w-3xl">
            <div className="rounded-2xl border border-slate-200 bg-white/60 p-6 shadow-sm sm:p-7">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-sage">
                  <Building2 className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">Entidades financieras</h3>
                  <p className="text-xs text-slate-500">Supervisadas por CNBV / CONDUSEF</p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {financieras.map((it) => <RegimenCard key={it.id} item={it} onWaitlist={openWaitlist} />)}
              </div>
            </div>
          </div>

          {/* Actividades Vulnerables (ancho completo — 17 giros del Art. 17 LFPIORPI) */}
          <div className="mt-10">
            <div className="rounded-2xl border border-slate-200 bg-white/60 p-6 shadow-sm sm:p-7">
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-sage">
                    <ShieldAlert className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900">Actividades Vulnerables</h3>
                    <p className="text-xs text-slate-500">
                      Las 17 fracciones del Art. 17 LFPIORPI — SAT / UIF
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="grid-vulnerables">
                {vulnerables.map((it) => <RegimenCard key={it.id} item={it} onWaitlist={openWaitlist} />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Por qué VORA */}
      <section className="bg-primary">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 sm:px-8 sm:py-28 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <DashboardMockup />
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-sm font-semibold uppercase tracking-wide text-sage">Por qué FELVORA Compliance</span>
            <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Software serio para áreas de cumplimiento y operación
            </h2>
            <ul className="mt-8 space-y-4">
              {BENEFICIOS.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage/20">
                    <CheckCircle2 className="h-4 w-4 text-sage" />
                  </span>
                  <span className="text-sm text-slate-100">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 4. Módulos */}
      <section id="modulos" className="bg-[#F7F7F2]">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-wide text-forest-600">Módulos</span>
            <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Todo lo que un sujeto obligado necesita para operar y cumplir
            </h2>
            <p className="mt-3 text-base text-slate-500">
              Una plataforma madre modular con motor normativo configurable, que se adapta al régimen de cada entidad.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {MODULOS.map((m) => (
              <div key={m.t} className="group rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-forest-600/40 hover:shadow-md">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-sage">
                  <m.icon className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <h3 className="mt-4 font-heading text-base font-semibold text-slate-900">{m.t}</h3>
                <p className="mt-1.5 text-sm text-slate-500">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4.5 Alcance del screening contra listas */}
      <section id="screening" className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-wide text-forest-600">
              PLD · Debida diligencia continua
            </span>
            <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Screening contra más de 200 listas
            </h2>
            <p className="mt-3 text-base text-slate-500">
              Cada cliente se coteja automáticamente al darlo de alta y se revisa de
              forma continua contra las listas oficiales.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Ban,
                titulo: "Personas bloqueadas",
                detalle: "Lista de Personas Bloqueadas de la UIF.",
              },
              {
                icon: Globe,
                titulo: "Sanciones internacionales",
                detalle: "OFAC, ONU y Unión Europea.",
              },
              {
                icon: UserCog,
                titulo: "PEP",
                detalle: "Personas Políticamente Expuestas, nacionales e internacionales.",
              },
              {
                icon: FileWarning,
                titulo: "SAT 69-B",
                detalle: "Estatus de presunto, definitivo y desvirtuado.",
              },
              {
                icon: Siren,
                titulo: "Fuerzas del orden",
                detalle: "Listas de procuración de justicia.",
              },
              {
                icon: Newspaper,
                titulo: "Información adversa",
                detalle: "Notas de prensa y fuentes públicas relevantes.",
              },
              {
                icon: MapPin,
                titulo: "Jurisdicciones de alto riesgo",
                detalle: "GAFI, OCDE y Unión Europea.",
              },
              {
                icon: ListChecks,
                titulo: "Listas propias",
                detalle: "Cada entidad mantiene sus propias listas internas.",
              },
            ].map((item) => (
              <div key={item.titulo}
                    className="rounded-xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-forest-600/40 hover:shadow-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <item.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="mt-3 font-heading text-sm font-semibold text-slate-900">
                  {item.titulo}
                </h3>
                <p className="mt-1 text-xs text-slate-500">{item.detalle}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-start gap-3 rounded-lg border border-slate-200 bg-[#F7F7F2] p-5">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-forest-600" strokeWidth={1.75} />
            <p className="text-sm text-slate-600">
              Resultado inmediato con semáforo de riesgo y evidencia guardada para auditoría.
            </p>
          </div>
        </div>
      </section>

      {/* 5. Centro de Integraciones */}
      <section id="integraciones" className="bg-primary">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 sm:px-8 sm:py-28 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage/10 px-3 py-1 text-xs font-medium text-sage">
              <Network className="h-3.5 w-3.5" /> Capa transversal
            </span>
            <h2 className="mt-5 font-heading text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Centro de Integraciones Regulatorias y Crediticias
            </h2>
            <p className="mt-4 text-base text-slate-200">
              Las APIs no son solo pantallas de configuración: alimentan, validan, actualizan y documentan
              procesos reales. Una consulta a Círculo o Buró alimenta el análisis de crédito, genera evidencia
              en el expediente, dispara alertas y queda en bitácora.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {["Círculo de Crédito", "Buró de Crédito", "Buró Empresas / PFAE", "CONDUSEF REUNE", "CONDUSEF REDECO", "Reporte de cartera a SIC"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-slate-100">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-sage" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: FileSearch, t: "Originación + SIC", d: "Autorización → consulta → análisis → comité." },
              { icon: Gauge, t: "Scoring asistido", d: "Recomendación preliminar sujeta a validación humana." },
              { icon: Layers, t: "Perfil 360", d: "Todo el cliente en una sola vista integrada." },
              { icon: Lock, t: "Control y evidencia", d: "Sin consulta sin autorización vigente." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-white/15 bg-white/5 p-5 backdrop-blur-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sage/15 text-sage">
                  <c.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-3 font-heading text-sm font-semibold text-white">{c.t}</h3>
                <p className="mt-1 text-xs text-slate-300">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Planes */}
      <section id="planes" className="bg-[#F7F7F2]">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wide text-forest-600">Planes</span>
            <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Planes por régimen
            </h2>
            <p className="mt-3 text-base text-slate-500">Elige tu actividad y consulta los planes diseñados para tu régimen.</p>
            <p className="mt-2 text-sm text-slate-500">
              Todos incluyen{" "}
              <a href="#screening"
                  data-testid="planes-link-screening"
                  className="font-medium text-forest-700 underline-offset-4 hover:underline">
                screening contra más de 200 listas
              </a>.
            </p>
            <div className="mx-auto mt-7 max-w-sm text-left">
              <Label className="mb-1.5 block text-xs font-medium text-slate-500">Tu actividad / régimen</Label>
              <Select value={planRegimen} onValueChange={setPlanRegimen}>
                <SelectTrigger data-testid="plan-regimen-select" className="h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Entidades financieras</SelectLabel>
                    {financieras.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.nombre_corto || f.nombre}{f.activo ? " · Disponible" : " · Próximamente"}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Actividades Vulnerables (LFPIORPI)</SelectLabel>
                    {vulnerables.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.fraccion ? `Fr. ${v.fraccion_display || v.fraccion} · ` : ""}{v.nombre_corto || v.nombre}
                        {v.activo ? " · Disponible" : " · Próximamente"}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          {planDisponible ? (
            esVulnerable ? (
              /* ago-2026 · plan ÚNICO de Actividad Vulnerable ($300/mes) para las 17
                 fracciones del Art. 17. El nombre y el precio salen del plan del backend:
                 se elimina la contradicción de titular la tarjeta con una sola fracción y
                 prometer las 17 en el subtítulo. */
              <div data-testid="plan-disponible" className="mt-12">
                {planesDelRegimen.map((p) => (
                  <div key={p.id} data-testid={`plan-${p.id}`}
                       className="mx-auto max-w-[640px] rounded-2xl border border-primary/30 bg-white p-8 shadow-md ring-1 ring-primary/10 transition-all">
                    <div className="text-center">
                      <p className="font-heading text-2xl font-semibold text-slate-900"
                         data-testid="plan-av-nombre">
                        {p.nombre}
                      </p>
                      <p className="mt-1 text-sm text-slate-500" data-testid="plan-av-alcance">
                        Aplica a las 17 fracciones del Art. 17 LFPIORPI
                      </p>
                      <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-forest-600/10 px-3 py-1 text-xs font-semibold text-forest-700"
                         data-testid="plan-av-regimen-elegido">
                        {regimenSel?.fraccion ? `Fr. ${regimenSel.fraccion_display || regimenSel.fraccion} · ` : ""}{nombreRegimenSel}
                      </p>
                      <p className="mt-5 font-heading text-4xl font-bold text-primary"
                         data-testid="plan-av-precio">
                        {peso(p.precio_mensual)}<span className="text-base font-normal text-slate-400">/mes</span>
                        <span className="ml-2 align-middle text-sm font-medium text-forest-700"
                              data-testid="plan-av-mas-iva">+ IVA</span>
                      </p>
                      <p className="mt-2 text-xs text-slate-500" data-testid="plan-av-incluye">
                        {p.creditos_incluidos} altas de clientes/mes con screening incluidas
                        {p.precio_credito_adicional ? ` · +${peso(p.precio_credito_adicional)} por alta adicional` : ""}
                      </p>
                    </div>
                    <div className="mt-8">
                      <p className="text-xs font-semibold uppercase tracking-wide text-forest-600">
                        Cumplimiento PLD incluido
                      </p>
                      <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {(p.modulos || []).map((it) => (
                          <li key={it} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-forest-600" /> {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link to={`/registro?regimen=${regimenSel?.id || ""}`} className="mt-8 block">
                      <Button data-testid={`plan-${p.id}-btn`}
                              className="w-full bg-primary hover:bg-sidebar-hover">
                        Registrar entidad
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div data-testid="plan-disponible">
                <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {planesDelRegimen.map((p) => (
                    <div key={p.id} className={`relative flex flex-col rounded-2xl border bg-white p-7 shadow-sm transition-all ${p.destacado ? "border-primary ring-2 ring-primary/20" : "border-slate-200"}`}>
                      {p.destacado && <span className="absolute -top-3 left-7 rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-sage">Recomendado</span>}
                      <p className="font-heading text-lg font-semibold text-slate-900">{p.nombre}</p>
                      <p className="text-sm text-slate-500">{p.desc}</p>
                      <p className="mt-4 font-heading text-3xl font-bold text-primary">
                        {planPrecio(p)}{!p.es_cotizacion && <span className="text-sm font-normal text-slate-400">/mes</span>}
                        {!p.es_cotizacion && (
                          <span className="ml-1.5 whitespace-nowrap align-middle text-xs font-normal text-slate-400"
                                data-testid={`precio-mas-iva-${p.id}`}>+ IVA</span>
                        )}
                      </p>
                      <ul className="mt-6 flex-1 space-y-2.5">
                        {planItems(p).map((it) => {
                          const fiduciario = it.toLowerCase().includes("fiduciario");
                          return (
                            <li key={it} className={`flex items-center gap-2 text-sm ${fiduciario ? "font-semibold text-forest-700" : "text-slate-600"}`}>
                              <CheckCircle2 className={`h-4 w-4 shrink-0 ${fiduciario ? "text-forest-700" : "text-forest-600"}`} /> {it}
                            </li>
                          );
                        })}
                      </ul>
                      <Link to={`/registro?regimen=${regimenSel?.id || ""}`} className="mt-7 block">
                        <Button data-testid={`plan-${p.id}-btn`} className={`w-full ${p.destacado ? "bg-primary hover:bg-sidebar-hover" : "bg-white text-primary border border-primary hover:bg-forest-600/5"}`}>
                          {p.es_cotizacion ? "Solicitar cotización" : "Comenzar"}
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
                <p className="mt-8 text-center text-sm text-slate-500">
                  Suscripción mensual · 2 meses gratis en plan anual. Cobro recurrente con Stripe.
                </p>
              </div>
            )
          ) : (
            <div data-testid="plan-proximamente" className="mx-auto mt-12 max-w-xl rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                <Clock className="h-3.5 w-3.5" /> Régimen en preparación
              </span>
              <h3 className="mt-5 font-heading text-xl font-semibold text-slate-900">
                Planes en preparación para {nombreRegimenSel}
              </h3>
              <p className="mx-auto mt-3 max-w-md text-sm text-slate-500">
                Estamos diseñando los planes y precios para este régimen. Déjanos tu correo y te avisamos
                con los precios al activar <span className="font-medium text-slate-700">{nombreRegimenSel}</span> en FELVORA.
              </p>
              <Button data-testid="plan-waitlist-btn" onClick={() => openWaitlist(nombreRegimenSel)} className="mt-7 bg-primary hover:bg-sidebar-hover">
                Unirme a la lista de espera
              </Button>
              <p className="mt-6 text-xs text-slate-400">
                Cobro recurrente con Stripe.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* 7. CTA */}
      <section className="bg-primary">
        <div className="mx-auto max-w-5xl px-5 py-20 text-center sm:px-8 sm:py-24">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Registra tu entidad y empieza hoy
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-200">
            El representante legal o administrador crea la entidad, captura datos corporativos y regulatorios,
            registra al Oficial de Cumplimiento y crea usuarios por área.
          </p>
          <Link to="/registro"><Button data-testid="cta-register" size="lg" className="mt-8 bg-forest-600 text-white hover:bg-forest-700">
            Crear cuenta de entidad <ArrowRight className="ml-2 h-4 w-4" />
          </Button></Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400">
        <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row">
            <div>
              <VoraLogo variant="horizontal" tone="light" />
              <p className="mt-3 max-w-md text-sm">
                Herramienta de gestión y apoyo al cumplimiento. El cumplimiento corresponde al sujeto obligado.
                Sistema configurable, sujeto a parametrización conforme a políticas internas.
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/login"><Button variant="outline" className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">Iniciar sesión</Button></Link>
              <Link to="/registro"><Button className="bg-primary hover:bg-sidebar-hover">Registrar entidad</Button></Link>
            </div>
          </div>
          <div className="mt-10 border-t border-slate-800 pt-6 text-xs text-slate-500">
            <p>No constituye certificación de CNBV, CONDUSEF, SAT o UIF, ni conexión con APIs oficiales en tiempo real. Plataforma preparada para integración con autoridades y sociedades de información crediticia.</p>
            <p className="mt-2">© {new Date().getFullYear()} FELVORA Compliance · RegTech para sujetos obligados.</p>
          </div>
        </div>
      </footer>

      {/* Dialog lista de espera */}
      <Dialog open={wlOpen} onOpenChange={setWlOpen}>
        <DialogContent className="sm:max-w-md" data-testid="waitlist-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Lista de espera — {wlRegimen}</DialogTitle>
            <DialogDescription>
              Déjanos tu correo y te avisaremos en cuanto el régimen <span className="font-medium text-slate-700">{wlRegimen}</span> esté disponible en FELVORA.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <Label className="mb-1 block text-xs">Correo electrónico</Label>
            <Input data-testid="waitlist-email" type="email" placeholder="tu@entidad.com" value={wlEmail}
              onChange={(e) => setWlEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitWaitlist()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWlOpen(false)}>Cancelar</Button>
            <Button data-testid="waitlist-submit" onClick={submitWaitlist} disabled={wlSending} className="bg-primary hover:bg-sidebar-hover">
              {wlSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Unirme a la lista
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
